import { ApolloClient, ApolloError, ApolloLink, from, HttpLink, InMemoryCache, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { v4 } from 'uuid';
import { GraphQLError } from 'graphql';

import { getGroupsFromUserInfo } from '../../components/management/AuthService';
import { getSavedImpersonatedUserId } from '../../components/management/AuthStorage';
import { getToken, getUserInfo } from '../../components/management/amplify-service';
import { getEnvConfig } from '../../envConfig';
import { genieUser } from '../../accessGuards';
import { apolloTraceIdHeader, requestIdHeader } from '../../setup/headers';
import { RetryLink } from './retryLink';
import { signUserOut } from 'components/technical/Appbar/AppbarProfile/AppBarProfile';

const envConfig = getEnvConfig();
const apiUrl = `${envConfig.apiHostnameAndPort}/graphql`;

console.debug('Using graphqlApi: ', apiUrl);
const api = new HttpLink({
  uri: apiUrl,
});

const stripTypename = <T>(key: string, value: T): undefined | T => {
  return key === '__typename' ? undefined : value;
};

const stripTypenameFromVariables = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = JSON.parse(JSON.stringify(operation.variables), stripTypename);
  }

  return forward(operation);
});

const createBackendError = (): ApolloError => {
  return new ApolloError({
    graphQLErrors: [
      new GraphQLError('Simulated graphql error', {
        extensions: {
          code: 'simulated error code',
          message: 'simulated error message',
        },
      }),
    ],
  });
};

// test apollo link to simulate backend errors for a single operation
const simulateBackendErrors = new ApolloLink((operation, forward) => {
  if (operation.operationName === 'testError') {
    return new Observable((observer) => {
      observer.error(createBackendError());
    });
  }

  return forward(operation);
});

const safeStringify = (val: unknown): unknown => {
  try {
    return JSON.stringify(val);
  } catch {
    return val;
  }
};

const logBackendErrorsLink = onError(({ graphQLErrors, networkError, operation }) => {
  for (const graphQLError of graphQLErrors ?? []) {
    console.error({
      msg: 'GraphQL error',
      error: safeStringify(graphQLError),
      operationName: operation.operationName,
      variables: safeStringify(operation.variables),
    });
  }

  if (networkError && networkError.name !== 'AbortError') {
    console.error({
      msg: 'GraphQL network error',
      error: safeStringify(networkError),
      operationName: operation.operationName,
      variables: safeStringify(operation.variables),
    });
  }
});

const logoutLink = onError(({ graphQLErrors }) => {
  if (!graphQLErrors) {
    return;
  }

  for (const error of graphQLErrors) {
    if (error.extensions?.code === 'not_authorized') {
      console.log('Signing user out after error from backend');
      signUserOut().then(
        () => {
          console.log('SignOut after backend error successful');
        },
        () => {
          console.error('Failed to sign user out after backend error');
        }
      );
    }
  }
});

// additional logging for operations
const logRequest = new ApolloLink((operation, forward) => {
  const requestContext = operation.getContext();
  const requestId = requestContext?.headers?.[requestIdHeader];
  console.log('request sent', {
    requestId,
    operation: operation.operationName,
  });
  return forward(operation).map((result) => {
    const responseContext = operation.getContext();
    const traceId = responseContext?.response?.headers?.get(apolloTraceIdHeader);
    console.log('response received', {
      requestId,
      traceId: traceId,
      operation: operation.operationName,
    });
    return result;
  });
});

const authentication = setContext(async (_req, { headers }) => {
  try {
    const [token, userInfo] = await Promise.all([getToken(), getUserInfo()]);

    const groups = userInfo ? getGroupsFromUserInfo(userInfo) : [];
    let impersonationId: null | string = null;
    if (genieUser(groups)) {
      impersonationId = getSavedImpersonatedUserId();
    }

    const extraHeaders: Record<string, string> = {
      authorization: token ? `Bearer ${token}` : '',
    };

    if (token) {
      const requestId = v4();
      // send request id only for logged in users
      extraHeaders[requestIdHeader] = requestId;
    }

    if (impersonationId && userInfo?.sub !== impersonationId) {
      extraHeaders['x-impersonated'] = impersonationId;
    }

    return {
      headers: {
        ...headers,
        ...extraHeaders,
      },
    };
  } catch (e) {
    console.error('Error from auth service', e);
    throw new Error('Auth service error');
  }
});

const retryLink = new RetryLink({
  delay: {
    initial: 500,
    jitter: true,
  },
  attempts: {
    max: 5,
  },
});

export default new ApolloClient({
  link: from(
    [
      authentication,
      logoutLink,
      stripTypenameFromVariables,
      simulateBackendErrors,
      retryLink,
      logBackendErrorsLink,
      getEnvConfig().logRequests ? logRequest : null,
      api,
    ].filter((val) => !!val)
  ),
  cache: new InMemoryCache({
    typePolicies: {
      Assets: {
        // singleton type
        keyFields: [],
        merge: true,
      },
      AssetDetails: {
        keyFields: false,
        merge: true,
      },
      AssetGroups: {
        keyFields: [],
      },
      Articles: {
        keyFields: [],
        merge: true,
      },
      Article: {
        merge: true,
      },
      ArticleToArticleLink: {
        keyFields: false,
      },
      DerivativeDetails: {
        keyFields: false,
        merge: true,
      },
      Bookkeeping: {
        keyFields: [],
      },
      Investments: {
        keyFields: [],
      },
      Calendar: {
        keyFields: [],
      },
      FactorRouterType: {
        keyFields: [],
      },
      FactorRouterTypeV2: {
        keyFields: [],
      },
      ScenarioAnalysisV2: {
        keyFields: [],
      },
      Management: {
        keyFields: [],
      },
      Notification: {
        keyFields: [],
      },
      PortfolioTimeWeightedReturnsType: {
        keyFields: false,
      },
      YieldOptimization: {
        keyFields: [],
      },
      TimeWeightedReturnType: {
        keyFields: false,
      },
      WeightedValueType: {
        keyFields: false,
        merge: true,
      },
      Portfolio: {
        keyFields: [],
        merge: true,
      },
      Journal: {
        keyFields: [],
      },
      PortfolioOptimizationResult: {
        merge: true,
      },
      PortfolioOptimization: {
        keyFields: [],
      },
      FundOptimization: {
        keyFields: [],
      },
      PortfolioDefinition: {
        merge: true,
      },
      FundOptimizationResult: {
        merge: true,
      },
      StressTestType: {
        keyFields: [],
      },
      Reconciliation: {
        keyFields: [],
      },
      RegimeType: {
        keyFields: [],
      },
      ReconciliationReportRow: {
        // prevents cache from computing an id. used when object is not a singleton, but we want to cache it as part of a parent
        keyFields: false,
      },
      NotCoveredJournalTransaction: {
        keyFields: false,
      },
      Strategy: {
        keyFields: false,
      },
      StrategyType: {
        keyFields: [],
      },
      NewsType: {
        keyFields: [],
      },
      AssetBulletPoints: {
        keyFields: [],
      },
      NavigatorGraph: {
        keyFields: [],
      },
      NewsFeed: {
        merge: true,
      },
      SubAccountPositions: {
        keyFields: false,
        merge: true,
      },
      MultiFactorQueryType: {
        keyFields: [],
        merge: true,
      },
      Agents: {
        keyFields: [],
        merge: true,
      },
      Conversation: {
        merge: true,
      },
    },
  }),
  devtools: {
    enabled: envConfig.apolloDevTools,
  },
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
    },
    query: {
      fetchPolicy: 'no-cache',
    },
    mutate: {
      fetchPolicy: 'no-cache',
    },
  },
});
